import { ArrowUpRight, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAddClientPayer, useInviteContact, useSetClientDefaultPayer, useStudioClient, useStudioMe, useStudioInvoices, useStudioProjects, useUpdateClientPayer } from '@/api/studio';
import type { Client, Invoice, LegalSchema, LegalValues, PayerSummary } from '@/api/types';
import { RequisitesFields } from '@/components/Requisites';
import { PersonLink } from '@/components/PersonDialog';
import { Avatar, Empty, Meta, PageHeader, Status } from '@/components/ui';
import { QueryPending } from '@/components/QueryState';
import { studioCan } from '@/lib/access';
import { blankLegal, countryName, taxIdLine, useStudioTerms } from '@/lib/contour';
import { formatDate, formatMoney } from '@/lib/format';
import { health, invoiceStatus } from '@/lib/status';
import { ClientHistory } from './AuditLog';
import { AddContact, ClientBasics } from './ClientEdit';
import { AccessRegistry, FeedbackSummary, OffboardDialog, Proposals, Signatures } from './ClientExtras';
import { ClientActs } from './ClientActs';
import { ClientIntegrations } from './ClientIntegrations';
import { InvoiceDraftActions } from './InvoiceDraftActions';
import { IssueInvoiceDialog } from './IssueInvoice';
import { MarkPaidDialog } from './MarkPaid';
import { markable } from './invoiceList';
import { StudioFiles } from './StudioFiles';

const roleLabel = { owner: 'владелец', member: 'сотрудник', accountant: 'бухгалтер' } as const;

/** One legal entity of the client: its requisites, editable by the studio (the client edits them in its settings). */
function PayerBlock({ client, payer, legal, many }: { client: Client; payer: PayerSummary; legal: LegalSchema; many: boolean }) {
  const update = useUpdateClientPayer();
  const setDefault = useSetClientDefaultPayer();
  const [form, setForm] = useState<LegalValues>();
  const shown = legal.fields
    .map((f) => ({ label: f.label, value: f.kind === 'country' ? countryName(payer.legal?.[f.key] ?? '') : (payer.legal?.[f.key] ?? '') }))
    .filter((x) => x.value);
  const error = update.error ?? setDefault.error;
  return (
    <div data-testid={`payer-${payer.id}`}>
      <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
        {many && <span className="text-sm font-medium">{payer.legalName || 'Название не указано'}</span>}
        {many && payer.isDefault && <span className="text-xs text-muted">основной</span>}
        {!form && (
          <span className="ml-auto flex gap-1">
            {many && !payer.isDefault && (
              <button className="btn-ghost h-7 px-2 text-xs" disabled={setDefault.isPending} onClick={() => setDefault.mutate({ clientId: client.id, payerId: payer.id })}>
                Сделать основным
              </button>
            )}
            <button className="btn-ghost h-7 px-2 text-xs" onClick={() => setForm(blankLegal(legal, payer.legal))}>
              Изменить
            </button>
          </span>
        )}
      </div>
      {form ? (
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            update.mutate({ clientId: client.id, payerId: payer.id, input: { legal: form } }, { onSuccess: () => setForm(undefined) });
          }}
        >
          <RequisitesFields legal={legal} values={form} onChange={(k, v) => setForm((x) => ({ ...x, [k]: v }))} markRequired={false} />
          {update.error && <p className="text-sm text-overdue">{(update.error as Error).message}</p>}
          <div className="flex gap-2">
            <button className="btn-primary" disabled={update.isPending}>
              Сохранить
            </button>
            <button type="button" className="btn-secondary" onClick={() => setForm(undefined)}>
              Отмена
            </button>
          </div>
        </form>
      ) : shown.length ? (
        <Meta items={shown} />
      ) : (
        <p className="text-sm text-muted">Не заполнены. Клиент может внести их сам в кабинете.</p>
      )}
      {!form && shown.length > 0 && payer.missing.length > 0 && <p className="mt-1 text-xs text-waiting">Не указан {payer.missing.join(', ')}: клиента попросили дополнить, счета выставляются и без этого.</p>}
      {!form && many && payer.projects.length > 0 && <p className="mt-1 text-xs text-muted">Проекты: {payer.projects.map((x) => x.title).join(', ')}</p>}
      {!form && setDefault.error && <p className="mt-1 text-sm text-overdue">{(error as Error).message}</p>}
    </div>
  );
}

/** The client's legal entities (payers) by the contour's schema; the default first. */
export function Requisites({ client, legal }: { client: Client; legal: LegalSchema }) {
  const payers: PayerSummary[] = client.payers?.length
    ? client.payers
    : [{ id: client.id, legal: client.legal, legalName: client.legalName, taxId: client.taxId, isDefault: true, missing: [], projects: [] }];
  const add = useAddClientPayer();
  const [adding, setAdding] = useState<LegalValues>();
  const many = payers.length > 1;
  return (
    <section>
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold">Реквизиты</h2>
        {!adding && (
          <button className="btn-ghost h-7 px-2 text-xs" onClick={() => setAdding(blankLegal(legal, {}))}>
            Добавить юрлицо
          </button>
        )}
      </div>
      <div className="space-y-5">
        {payers.map((p) => (
          <PayerBlock key={p.id} client={client} payer={p} legal={legal} many={many} />
        ))}
      </div>
      {adding && (
        <form
          className="mt-5 grid gap-3 border-t border-line pt-4"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate({ clientId: client.id, input: { legal: adding } }, { onSuccess: () => setAdding(undefined) });
          }}
        >
          <h3 className="text-sm font-medium">Новое юрлицо</h3>
          <RequisitesFields legal={legal} values={adding} onChange={(k, v) => setAdding((x) => ({ ...x, [k]: v }))} markRequired={false} />
          {add.error && <p className="text-sm text-overdue">{(add.error as Error).message}</p>}
          <div className="flex gap-2">
            <button className="btn-primary" disabled={add.isPending}>
              Добавить
            </button>
            <button type="button" className="btn-secondary" onClick={() => setAdding(undefined)}>
              Отмена
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

export function StudioClient() {
  const { clientId = '' } = useParams();
  const clientQ = useStudioClient(clientId);
  const c = clientQ.data;
  const projectsQ = useStudioProjects(clientId);
  const projects = projectsQ.data;
  const { data: invoices } = useStudioInvoices(clientId);
  const invite = useInviteContact();
  const { legal } = useStudioTerms();
  const me = useStudioMe();
  const [billing, setBilling] = useState(false);
  const [offboarding, setOffboarding] = useState(false);
  const [marking, setMarking] = useState<Invoice>();
  const canMoney = !!me && studioCan(me.studioRole, 'money');

  if (!c) return <QueryPending query={clientQ} />;

  return (
    <>
      <PageHeader
        back={
          <Link to="/studio/clients" className="text-sm text-muted hover:text-ink">
            ← Клиенты
          </Link>
        }
        title={c.name}
        description={[c.legalName, legal ? taxIdLine(legal, c.legal ?? { taxId: c.taxId }) : '', c.contour === 'ru' ? 'контур РФ' : 'основной контур'].filter(Boolean).join(', ')}
        actions={
          <>
            {c.id === 'karandash' && (
              <a className="btn-secondary" href="/" target="_blank" rel="noreferrer">
                Как видит клиент <ArrowUpRight className="size-4" />
              </a>
            )}
            {c.stage !== 'former' && (
              <button className="btn-ghost" onClick={() => setOffboarding(true)}>
                Завершить работу
              </button>
            )}
            <button className="btn-secondary" onClick={() => setBilling(true)} disabled={!projects}>
              Выставить счёт
            </button>
            <Link className="btn-primary" to={`/studio/projects/new?client=${c.id}`}>
              <Plus className="size-4" /> Новый проект
            </Link>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0 space-y-10">
          <Proposals client={c} />
          <section>
            <h2 className="mb-2 text-sm font-semibold">Проекты</h2>
            {!projects ? (
              <QueryPending query={projectsQ} compact />
            ) : projects.length === 0 ? (
              <Empty title="Проектов нет">Создайте первый: после этого клиенту будет что показать в кабинете.</Empty>
            ) : (
              <ul className="divide-y divide-line border-y border-line">
                {projects.map((p) => {
                  const h = health[p.health];
                  return (
                    <li key={p.id}>
                      <Link to={`/studio/projects/${p.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3 hover:bg-panel">
                        <span className="min-w-0 flex-1 max-sm:basis-full">
                          <span className="block font-medium">{p.title}</span>
                          <span className="block truncate text-sm text-muted">{p.now}</span>
                        </span>
                        <span className="text-sm text-muted">{p.kind === 'fixed' ? 'фикс' : 'регулярные'}</span>
                        <span className="num text-right text-sm sm:w-28">{formatMoney(p.terms.price)}</span>
                        <Status tone={h.tone} className="ml-auto sm:w-40 sm:justify-end">
                          {h.label}
                        </Status>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section>
            <h2 className="mb-2 text-sm font-semibold">Счета</h2>
            {invoices?.length ? (
              <ul className="divide-y divide-line border-y border-line">
                {invoices.map((i) => {
                  const s = invoiceStatus[i.status];
                  return (
                    <li key={i.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2.5 text-sm">
                      <span className="flex-1 max-sm:basis-full">
                        № {i.number} <span className="text-muted">от {formatDate(i.issuedAt)}, {i.lines[0]?.title}{(c.payers?.length ?? 0) > 1 && i.payer ? `, ${i.payer.name}` : ''}</span>
                      </span>
                      <span className="num">{formatMoney(i.total)}</span>
                      {markable(i) && canMoney && (
                        <button className="btn-ghost h-7 px-2 text-xs" onClick={() => setMarking(i)}>
                          Отметить оплату
                        </button>
                      )}
                      <Status tone={s.tone} className="ml-auto sm:w-36 sm:justify-end">
                        {s.label}
                      </Status>
                      {i.status === 'draft' && <InvoiceDraftActions invoice={i} />}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-muted">Счетов не было.</p>
            )}
          </section>
          {projects && <ClientActs client={c} projects={projects} />}
          {projects && <Signatures client={c} projects={projects} />}
          <StudioFiles clientId={c.id} />
          <AccessRegistry client={c} />
          <ClientIntegrations client={c} canSwitch={canMoney} />
          {me && studioCan(me.studioRole, 'team') && <ClientHistory clientId={c.id} />}
        </div>

        <aside className="space-y-8">
          {me && studioCan(me.studioRole, 'clients') && <ClientBasics key={`${c.name}/${c.stage}/${c.manager.id}`} client={c} />}
          <section>
            <h2 className="mb-3 text-sm font-semibold">Люди клиента</h2>
            <ul className="space-y-3">
              {c.contacts.map((x) => (
                <li key={x.email} className="flex items-center gap-3">
                  <Avatar person={x.person} />
                  <div className="min-w-0 flex-1 leading-tight">
                    <div className="truncate text-sm font-medium">
                      <PersonLink person={x.person} />
                    </div>
                    <div className="truncate text-xs text-muted">
                      {roleLabel[x.role]}, {x.email}
                    </div>
                    {(x.phone || x.messenger) && <div className="truncate text-xs text-muted">{[x.phone, x.messenger].filter(Boolean).join(' · ')}</div>}
                  </div>
                  {x.invited === 'pending' ? (
                    <button className="btn-ghost h-7 px-2 text-xs" onClick={() => invite.mutate({ clientId: c.id, email: x.email })}>
                      Отправить ещё раз
                    </button>
                  ) : (
                    <span className="text-xs text-done">в кабинете</span>
                  )}
                </li>
              ))}
            </ul>
            {me && studioCan(me.studioRole, 'clients') && (
              <div className="mt-3">
                <AddContact client={c} />
              </div>
            )}
          </section>
          {legal && <Requisites client={c} legal={legal} />}
          {c.id === 'karandash' && <FeedbackSummary />}
          <Meta
            items={[
              { label: 'Ведёт', value: <PersonLink person={c.manager} /> },
              { label: 'С нами с', value: formatDate(c.since) },
            ]}
          />
        </aside>
      </div>
      <MarkPaidDialog invoice={marking} onClose={() => setMarking(undefined)} />
      {offboarding && <OffboardDialog client={c} onClose={() => setOffboarding(false)} />}
      {projects && <IssueInvoiceDialog clientId={c.id} projects={projects} open={billing} onClose={() => setBilling(false)} />}
    </>
  );
}
